import { Injectable } from '@angular/core'
import { UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms'
import { OCC_USER_ID_ANONYMOUS, UserIdService } from '@spartacus/core'
import { UserProfileService } from '@spartacus/user/profile/core'
import { Title } from '@spartacus/user/profile/root'
import { combineLatest, Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import {
  EbFormElementsConfig,
  EbFormGroupConfig
} from '../../../../form/interfaces/eb-form.interface'
import { EbFormService } from '../../../../form/services/eb-form.service'
import { EbCommonFormFieldDataPolicyComponent } from '../../components/common-form-field-data-policy/eb-common-form-field-data-policy.component'
import { EbCommonFormRequiredHintComponent } from '../../components/common-form-required-hint/eb-common-form-required-hint.component'
import { EbPersonalInfoInterface } from '../../interfaces/eb-common-form.interface'
import { EbCommonFormAdapter } from '../connectors/eb-common-form.adapter'

@Injectable()
export class EbCommonFormConfigService {
  constructor(
    protected userIdService: UserIdService,
    protected ebFormService: EbFormService,
    protected adapter: EbCommonFormAdapter,
    protected userProfileService: UserProfileService
  ) {}

  static customerValidationFactory() {
    return (formGroup: UntypedFormGroup): ValidationErrors => {
      const customerControl = formGroup.get('customerNumber')
      const customerUnkownControl = formGroup.get('customerNumberUnknown')
      if (customerControl.value || customerUnkownControl.value) {
        customerControl.setErrors(null)
        customerUnkownControl.setErrors(null)
        return null
      } else {
        const error = { invalid: 'missing.customer' }
        customerControl.setErrors([error])
        customerUnkownControl.setErrors([error])
        return error
      }
    }
  }

  static createCustomerUnknownDependentValidation = (dependentControlNames: string[]) => {
    return (formGroup: UntypedFormGroup): ValidationErrors => {
      const masterControl = formGroup.get('customerNumberUnknown')
      let validationError = null
      dependentControlNames.forEach(name => {
        const control = formGroup.get(name)
        if (!control) {
          throw new Error(`Can't find control '${name}' in FormGroup`)
        }
        if (masterControl.value) {
          const error = Validators.required(control)
          if (error) {
            validationError = { invalid: 'missing.customer' }
          }
          control.setErrors(error)
        } else {
          control.setErrors(null)
        }
      })
      return validationError
    }
  }

  getPersonalInfoGroupConfig(): Observable<EbFormGroupConfig> {
    return this.getCustomerFields().pipe(
      map(
        personalInfoFields =>
          ({
            name: 'personalInfo',
            type: 'group',
            hideTitle: true,
            elements: [...personalInfoFields],
            validations: [
              EbCommonFormConfigService.customerValidationFactory(),
              EbCommonFormConfigService.createCustomerUnknownDependentValidation([
                'companyName',
                'country',
                'street',
                'houseNumber',
                'city',
                'postalCode'
              ])
            ]
          }) as EbFormGroupConfig
      )
    )
  }

  getCustomerFields(path = 'personalInfo'): Observable<EbFormElementsConfig> {
    return combineLatest([
      this.adapter.getCountries(),
      this.getCustomerInfo(),
      this.getTitles()
    ]).pipe(
      map(([countries, personalInfo, titles]) => {
        const CUSTOMER_NUMBER_UNKOWN_PATH = (path ? path + '.' : '') + 'customerNumberUnknown'
        return [
          {
            name: 'title',
            type: 'field',
            value: personalInfo.title ?? '',
            fieldType: 'select',
            required: true,
            options: titles.map(title => ({
              name: title.code,
              label: { default: title.name }
            }))
          },
          {
            name: 'firstName',
            type: 'field',
            value: personalInfo.firstName ?? '',
            fieldType: 'input',
            required: true
          },
          {
            name: 'lastName',
            type: 'field',
            value: personalInfo.lastName ?? '',
            fieldType: 'input',
            required: true
          },
          {
            name: 'email',
            type: 'field',
            value: personalInfo.email ?? '',
            fieldType: 'input',
            required: true
          },
          {
            name: 'phone',
            type: 'field',
            value: personalInfo.phone ?? '',
            fieldType: 'input',
            required: true
          },
          {
            name: 'customerNumber',
            type: 'field',
            value: personalInfo.customerNumber ?? '',
            fieldType: 'input',
            dependsOn: this.ebFormService.createSimpleDependsOn(
              CUSTOMER_NUMBER_UNKOWN_PATH,
              '1',
              'disable',
              'checkValue'
            )
          },
          {
            name: 'customerNumberUnknown',
            type: 'field',
            hideTitle: true,
            value: personalInfo.customerNumberUnknown ?? false,
            fieldType: 'checkbox',
            options: [{ labelToken: 'customerNumberUnknown', name: '1' }]
          },
          {
            name: 'companyName',
            type: 'field',
            value: personalInfo.companyName ?? '',
            fieldType: 'input',
            dependsOn: this.ebFormService.createSimpleDependsOn(CUSTOMER_NUMBER_UNKOWN_PATH, '1')
          },
          {
            name: 'country',
            type: 'field',
            value: personalInfo.country ?? '',
            fieldType: 'datalist',
            options: (() => {
              const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
              return countries
                .map(country => ({
                  label: { default: country.name },
                  name: country.isocode
                }))
                .sort((a, b) => collator.compare(a.label.default, b.label.default))
            })(),
            dependsOn: this.ebFormService.createSimpleDependsOn(CUSTOMER_NUMBER_UNKOWN_PATH, '1')
          },
          {
            name: 'street',
            type: 'field',
            value: personalInfo.street ?? '',
            fieldType: 'input',
            dependsOn: this.ebFormService.createSimpleDependsOn(CUSTOMER_NUMBER_UNKOWN_PATH, '1')
          },
          {
            name: 'houseNumber',
            type: 'field',
            value: personalInfo.houseNumber ?? '',
            fieldType: 'input',
            dependsOn: this.ebFormService.createSimpleDependsOn(CUSTOMER_NUMBER_UNKOWN_PATH, '1')
          },
          {
            name: 'city',
            type: 'field',
            value: personalInfo.city ?? '',
            fieldType: 'input',
            dependsOn: this.ebFormService.createSimpleDependsOn(CUSTOMER_NUMBER_UNKOWN_PATH, '1')
          },
          {
            name: 'postalCode',
            type: 'field',
            value: personalInfo.postalCode ?? '',
            fieldType: 'input',
            dependsOn: this.ebFormService.createSimpleDependsOn(CUSTOMER_NUMBER_UNKOWN_PATH, '1')
          },
          {
            name: 'requiredInfo',
            type: 'component',
            component: EbCommonFormRequiredHintComponent
          }
        ]
      })
    )
  }

  getPrivacyAndCaptchaFieldsConfig(): Observable<EbFormElementsConfig> {
    return of([
      {
        name: 'approvalOfDataPolicy',
        type: 'field',
        value: '',
        fieldType: 'component',
        required: true,
        options: [
          {
            name: '1',
            labelToken: 'label'
          }
        ],
        component: EbCommonFormFieldDataPolicyComponent
      },
      {
        hideTitle: true,
        name: 'captcha',
        type: 'field',
        value: '',
        fieldType: 'recaptcha',
        required: true,
        meta: {
          siteKey: '6LdVt9QbAAAAANxgI95S99Q-Y5J3HDK9zd5tK10t'
        }
      }
    ])
  }

  protected getCustomerInfo(): Observable<EbPersonalInfoInterface> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        if (userId !== OCC_USER_ID_ANONYMOUS) {
          return this.adapter.getCustomerInfo(userId)
        } else {
          return of({} as EbPersonalInfoInterface)
        }
      })
    )
  }

  protected getTitles(): Observable<Title[]> {
    return this.userProfileService.getTitles()
  }
}
